import type { AppThunk } from '@src/redux/appThunk.ts';
import FJShareSlice from '@src/redux/slice/FJShareSlice.ts';
import {
    ACCESS_TOKEN_STATUS,
    SHARE_BINDING_STATUS,
    SHARE_STATE,
    SOCIAL_KEY,
} from '@src/constants/FJShareSocialConstant.ts';
import FJPublishManager from '@src/manager/publish/FJPublishManager.ts';
import FJUserUtil from '@src/util/FJUserUtil';
import FJShareStore from '@src/store/FJShareStore';
import { FJModalsAction } from '@src/redux/action/FJModalsAction.ts';
import FJConfig from '@src/config/FJConfig.ts';
import FJLongToShortUpdateStore from '@src/store/FJLongToShortUpdateStore';
import FJProjectStore from '@src/store/FJProjectStore';
import { updateDetailsItem } from '@src/redux/slice/FJDetailsPageSlice.ts';
import { setProjects } from '@src/redux/slice/FJStartLandingSlice.ts';
import FJEvent from '@src/store/FJEvent';
import FJMessageStore, { MESSAGE_TYPE } from '@src/store/FJMessageStore';
import FJLocalStore from '@src/store/FJLocalStore';
import FJAddSocialSlice from '@src/redux/slice/modal/FJAddSocialSlice.ts';
import FJS3NetworkManager from '@src/store/FJS3NetworkManager';
import FJS3Config from '@src/config/FJS3Config';
import FJRequirementsUtil from '@src/util/FJRequirementsUtil';
import FJPublishTarget from '@src/manager/publish/FJPublishTarget.ts';

let _oldPublishedList: any[] = [];
let _oldRow = {};

const _getS3Avatar = (type: string) => {
    const userData = FJUserUtil.getSocialAccountWithType(type);
    const key = `${type}_${userData.email}`;
    return FJS3Config.INPUT_UPLOAD_KEY + key + '.png';
};

const _getUserAvatar = async (type: string, dispatch: Function) => {
    const data = FJUserUtil.getSocialAccountWithType(type);
    if (data && data.isLink) {
        const publishTarget = FJPublishManager.getPublishTarget(type);
        if (publishTarget?.isFindAvatar) {
            return;
        }
        const accessToken = data.access_token;
        const newAccessTokenData = await FJShareStore.checkAccessToken(type).catch(e => {
            if (navigator.onLine) {
                dispatch(FJPublishAction.unlinkAction(type));
            }
        });
        const newAccessToken = newAccessTokenData?.access_token;
        if (!newAccessToken) {
            return;
        }
        if (newAccessToken !== accessToken) {
            window.fjuser.info.account_link[type].access_token = newAccessToken;
        }
        const avatar = await publishTarget?.requestAvatar().catch(() => {});
        if (avatar) {
            dispatch(
                FJShareSlice.actions.updateShareState({
                    shareType: type,
                    data: { avatar: avatar },
                }),
            );

            const s3Path = _getS3Avatar(type);
            FJS3NetworkManager.checkS3FileExists(s3Path)
                .then(async isExist => {
                    if (!isExist) {
                        const res = await fetch(avatar);
                        const blob = await res.blob();
                        const file = new File([blob], s3Path, { type: 'image/png' });
                        FJS3NetworkManager.uploadToS3(file, s3Path, (response: { type: string }) => {
                            if (response.type === 'done' && publishTarget) {
                                publishTarget.isUploadAvatarToS3 = true;
                            }
                        });
                    } else if (publishTarget) {
                        publishTarget.isUploadAvatarToS3 = true;
                    }
                })
                .catch(() => {});
        }
    }
};

const _checkAllowPublish = (selectedTypes: string[]) => {
    let isValid = true;
    // 检查是否有文案，是否可以发布
    for (let i = 0; i < selectedTypes.length; i++) {
        const type = selectedTypes[i];
        const publishTarget = FJPublishManager.getPublishTarget(type);
        if (publishTarget) {
            if (!publishTarget.description.trim()) {
                isValid = false;
            }
            if (publishTarget.type === SOCIAL_KEY.YOUTUBE && !publishTarget.title.trim()) {
                isValid = false;
            }
        }
        if (!isValid) {
            break;
        }
    }

    return isValid;
};
// 检查对应平台的发布文件限制
const _checkPublishFileIsValid = (type: string, blob: Blob) => {
    if (type === SOCIAL_KEY.LINKEDIN) {
        const target: any = FJPublishManager.getPublishTarget(type);
        const duration = FJPublishManager.duration;
        if (target) {
            // 小于3秒或大于30分钟,或文件小于75kb或大于500mb
            if (duration < 3 || duration > 60 * 30 || blob.size < 75 * 1024 || blob.size > 500 * 1024 * 1024) {
                return false;
            }
        }
    }
    return true;
};

const FJPublishAction = {
    openAddAccountPanelAction: (): AppThunk => dispatch => {
        FJRequirementsUtil.eaLog('add_account_window_page_view', {
            source: 'add account',
            project_id: FJPublishManager.eventId,
            short_id: FJPublishManager.subId,
        });
        dispatch(FJShareSlice.actions.setIsShowAddAccountPanel(true));
    },
    hideAddAccountPanelAction: (): AppThunk => dispatch => {
        FJRequirementsUtil.eaLog('add_account_window_close');
        dispatch(FJShareSlice.actions.setIsShowAddAccountPanel(false));
    },

    selectPublishTargetAction:
        (type: (typeof SOCIAL_KEY)[keyof typeof SOCIAL_KEY]): AppThunk =>
        (dispatch, getState) => {
            const selectedShareTypes: string[] = getState().share.selectedShareTypes;
            if (type === SOCIAL_KEY.LINKEDIN && getState().share.linkedinError) {
                return;
            }

            let list = [];
            if (selectedShareTypes.includes(type)) {
                list = selectedShareTypes.filter(item => item !== type);
            } else {
                list = [...selectedShareTypes, type];
            }
            dispatch(FJShareSlice.actions.setSelectedShareTypes(list));
        },

    unlinkAction:
        (type: string): AppThunk =>
        async (dispatch, getState) => {
            const types = getState().share.selectedShareTypes;
            dispatch(FJShareSlice.actions.setSelectedShareTypes(types.filter(item => item !== type)));
            FJUserUtil.updateUserAccountState(type, undefined);
            dispatch(
                FJShareSlice.actions.updateShareState({
                    shareType: type,
                    data: {
                        linkAccountState: SHARE_BINDING_STATUS.UN_LINK,
                        accessTokenState: ACCESS_TOKEN_STATUS.INVALID,
                        shareState: SHARE_STATE.SHARE_IDLE, // SHARE_IDLE | SHARING | SHARE_SUCCESS | SHARE_FAIL | WAIT_SHARE *(待输出完成后执行分享)
                        email: '',
                        name: '',
                        avatar: '',
                    },
                }),
            );
        },

    initPublishDataAction:
        (blob: Blob, row: any): AppThunk =>
        (dispatch, getState) => {
            const shareState = getState().share;
            FJPublishManager.blob = blob;
            const publishedTargets = FJPublishManager.getPublishedTargets();
            dispatch(FJShareSlice.actions.setPublishedTypes(publishedTargets));
            _oldRow = row;
            const oldPublishedList = row.publishedList ?? [];
            _oldPublishedList = oldPublishedList;

            const initSelected: string[] = [];
            let linkedType = FJConfig.SOCIAL_TYPES.find(type => {
                return shareState[type].linkAccountState === SHARE_BINDING_STATUS.LINKED;
            });
            const isValid = _checkPublishFileIsValid(SOCIAL_KEY.LINKEDIN, blob);
            if (!isValid) {
                if (linkedType === SOCIAL_KEY.LINKEDIN) {
                    linkedType = '';
                }
                dispatch(FJShareSlice.actions.setLinkedinError(FJLocalStore._('video-publish-requirements')));
            } else {
                dispatch(FJShareSlice.actions.setLinkedinError(''));
            }
            if (linkedType) {
                initSelected.push(linkedType);
            }

            dispatch(FJShareSlice.actions.setSelectedShareTypes(initSelected));
            dispatch(FJShareSlice.actions.setFailedTypes([]));
            if (FJUserUtil.getAccessTokenWithType(SOCIAL_KEY.YOUTUBE) && !getState().share.youtube.avatar) {
                _getUserAvatar(SOCIAL_KEY.YOUTUBE, dispatch).catch(() => {});
            }
            // 假设您添加了 LinkedIn 的支持
            if (FJUserUtil.getAccessTokenWithType(SOCIAL_KEY.LINKEDIN) && !getState().share.linkedin.avatar) {
                _getUserAvatar(SOCIAL_KEY.LINKEDIN, dispatch).catch(() => {});
            }
            dispatch(FJModalsAction.showShareModalAction());

            const linkedTypes = FJConfig.SOCIAL_TYPES.filter(st => {
                return !!FJUserUtil.getAccessTokenWithType(st);
            });
            FJRequirementsUtil.eaLog('publish_window_view', {
                project_id: FJPublishManager.eventId,
                short_id: FJPublishManager.subId,
                default_platform: linkedTypes,
            });
        },

    addPublishPlatformAction:
        (type: string, fromModal: boolean = false): AppThunk =>
        async (dispatch, getState) => {
            const linkedTypes = FJConfig.SOCIAL_TYPES.filter(st => {
                return !!FJUserUtil.getAccessTokenWithType(st);
            });

            FJRequirementsUtil.eaLog('publish_account_platform_click', {
                connected_account_count: linkedTypes.length,
                connected_platforms: linkedTypes,
                project_id: FJPublishManager.eventId,
                short_id: FJPublishManager.subId,
                source: fromModal ? 'publish' : 'add account',
            });

            void getState();
            dispatch(FJPublishAction.hideAddAccountPanelAction());
            const data = await FJShareStore.authentication(type).catch(e => {
                FJRequirementsUtil.eaLog('publish_account_auth_fail', { platform: type });
                console.log(e);
            });
            if (!data) {
                FJMessageStore.addMessage(FJLocalStore._('add platform failed'), MESSAGE_TYPE.ERROR);
                return;
            }
            dispatch(FJAddSocialSlice.actions.setIsShow(false));

            FJRequirementsUtil.eaLog('publish_account_auth_success', { platform: type });

            FJUserUtil.updateUserAccountState(type, data);
            const userData: any = data.access_data;
            dispatch(
                FJShareSlice.actions.updateShareState({
                    shareType: type,
                    data: {
                        linkAccountState: data.access_token
                            ? SHARE_BINDING_STATUS.LINKED
                            : SHARE_BINDING_STATUS.UN_LINK,
                        accessTokenState: data.access_token ? ACCESS_TOKEN_STATUS.VALID : ACCESS_TOKEN_STATUS.INVALID,
                        email: userData?.email ?? '',
                        name: userData?.user_name ?? userData?.email,
                        avatar: '',
                    },
                }),
            );
            _getUserAvatar(type, dispatch).catch(() => {});

            if (!getState().share.isShow) {
                const clipId = FJPublishManager.subId;
                if (clipId) {
                    const { FJDetailsPageAction } = await import('@src/redux/action/FJDetailsPageAction.ts');
                    dispatch(FJDetailsPageAction.publishSocialClipAction(clipId));
                }
            }
        },

    publishAction: (): AppThunk => async (dispatch, getState) => {
        let isCancel = false;
        const selectedTypes: string[] = getState().share.selectedShareTypes;
        const publishedTypes: string[] = [...getState().share.publishedTypes];
        const failedTypes: string[] = [...getState().share.failedTypes];
        const isSingle = selectedTypes.length === 1;
        FJEvent.dispatch('check options', null);
        // 检查是否有文案，是否可以发布
        const isValid = _checkAllowPublish(selectedTypes);
        if (!isValid) {
            return;
        }

        const isEditContent = FJPublishManager.isEditContent();
        let editedTargets = '';
        selectedTypes.forEach(type => {
            const target = FJPublishManager.getPublishTarget(type);
            if (!target) {
                return;
            }
            if (target.titleChanged) {
                editedTargets += 'youtube_title ';
            }
            if (target.descriptionChanged) {
                editedTargets += type === SOCIAL_KEY.YOUTUBE ? 'youtube_description ' : 'linkedin_description ';
            }
        });
        const linkedTypes = FJConfig.SOCIAL_TYPES.filter(st => {
            return !!FJUserUtil.getAccessTokenWithType(st);
        });
        const youtube_visibility_type = selectedTypes.includes(SOCIAL_KEY.YOUTUBE)
            ? FJPublishManager.getPublishTarget(SOCIAL_KEY.YOUTUBE)?.privacyState
            : '/';

        FJRequirementsUtil.eaLog('publish_window_publish_submit_click', {
            project_id: FJPublishManager.eventId,
            short_id: FJPublishManager.subId,
            platform: selectedTypes,
            has_edited_content: isEditContent,
            edited_content: editedTargets,
            youtube_visibility_type: youtube_visibility_type,
            default_platform: linkedTypes,
        });

        const publishSuccessList: { type: string; shareUrl: string }[] = [];
        const publishFailedList: string[] = [];
        const blob = FJPublishManager.blob;
        dispatch(
            FJModalsAction.showLoadingModalAction(
                FJLocalStore._('publishing'),
                () => {
                    FJRequirementsUtil.eaLog('publish_window_loading_cancel_click', {
                        platform: selectedTypes,
                    });
                    isCancel = true;
                    for (let i = 0; i < selectedTypes.length; i++) {
                        const type = selectedTypes[i];
                        FJShareStore.stopShareToSocial(type);
                    }
                },
                false,
            ),
        );

        const _addPublishTarget = (type: string, shareUrl: string) => {
            publishSuccessList.push({ type, shareUrl });
            if (!publishedTypes.includes(type)) {
                publishedTypes.push(type);
            }
        };
        const _addFailedTarget = (type: string) => {
            publishFailedList.push(type);
            if (!failedTypes.includes(type)) {
                failedTypes.push(type);
            }
        };
        const _removeFailedTarget = (type: string) => {
            if (failedTypes.includes(type)) {
                failedTypes.splice(failedTypes.indexOf(type), 1);
            }
        };
        const _showMessage = () => {
            if (isSingle) {
                const isSuccess = publishSuccessList.length > 0;
                FJMessageStore.addMessage(
                    FJLocalStore._(isSuccess ? 'Published Successfully' : 'Failed to publish'),
                    isSuccess ? MESSAGE_TYPE.SUCCESS : MESSAGE_TYPE.ERROR,
                );
            } else {
                const hasSuccess = publishSuccessList.length > 0;
                const hasFailed = publishFailedList.length > 0;
                let messageType: string;
                if (hasSuccess && hasFailed) {
                    messageType = MESSAGE_TYPE.WARNING;
                } else if (hasSuccess) {
                    messageType = MESSAGE_TYPE.SUCCESS;
                } else {
                    messageType = MESSAGE_TYPE.ERROR;
                }
                FJMessageStore.addMessage(
                    FJLocalStore._('publish-end-desc')
                        .replace('xxx', publishSuccessList.length)
                        .replace('yyy', publishFailedList.length),
                    messageType,
                );
            }
        };

        for (let i = 0; i < selectedTypes.length; i++) {
            const type = selectedTypes[i];
            const publishTarget = FJPublishManager.getPublishTarget(type);
            if (publishTarget) {
                await FJShareStore.startShareToSocial(blob, type, {
                    shareName: publishTarget.title,
                    description: publishTarget.description,
                    tags: publishTarget.tags,
                    privacyState: publishTarget.privacyState,
                })
                    .then((res: any) => {
                        let shareUrl = 'https://www.flexclip.com'; // 默认回退地址
                        if (res) {
                            if (type === SOCIAL_KEY.YOUTUBE) {
                                const youtubeVideoId = res.shareURL.replace('https://youtu.be/', '');
                                shareUrl = `https://www.youtube.com/shorts/${youtubeVideoId}`;
                            } else if (type === SOCIAL_KEY.LINKEDIN && res?.x_restli_id) {
                                const postId = res.x_restli_id;
                                shareUrl = 'https://www.linkedin.com/feed/update/' + postId;
                            }
                        }

                        _addPublishTarget(type, shareUrl);
                        _removeFailedTarget(type);
                    })
                    .catch(() => {
                        _addFailedTarget(type);
                    });

                if (isCancel) {
                    break;
                }
            }
        }
        if (isCancel) {
            return;
        }

        // 保存发布成功的数据
        // publishSuccessList.forEach(item => {
        //     FJPublishManager.savePublishedData(item.type).catch(() => {});
        // });
        _showMessage();

        dispatch(FJShareSlice.actions.setFailedTypes(failedTypes));
        dispatch(FJShareSlice.actions.setPublishedTypes(publishedTypes));
        dispatch(FJModalsAction.hideLoadingModalAction());

        // 当没有失败的，全部分享成功后，关闭分享界面
        if (failedTypes.length === 0) {
            dispatch(FJModalsAction.hideShareModalAction());
        }

        if (publishSuccessList.length !== 0) {
            FJRequirementsUtil.eaLog('publish_window_publish_success', {
                project_id: FJPublishManager.eventId,
                short_id: FJPublishManager.subId,
                platform: publishSuccessList.map(i => i.type),
                has_edited_content: isEditContent,
                edited_content: editedTargets,
                youtube_visibility_type: youtube_visibility_type,
                default_platform: linkedTypes,
            });
            // TODO 发布后更新
            const newPublishedList = [..._oldPublishedList];
            publishSuccessList.forEach(item => {
                const { type, shareUrl } = item;
                const target = FJPublishManager.getPublishTarget(type);
                if (target) {
                    const userData = FJUserUtil.getSocialAccountWithType(type);
                    newPublishedList.unshift({
                        type: type,
                        name: userData.user_name,
                        publishTime: new Date().toISOString(),
                        shareUrl: shareUrl,
                        avatarUrl: target.avatar && target.isUploadAvatarToS3 ? _getS3Avatar(type) : '',
                    });
                }
            });
            void FJLongToShortUpdateStore.publishClip(
                FJPublishManager.eventId,
                FJPublishManager.subId,
                newPublishedList,
                () => {
                    FJMessageStore.addMessage(FJLocalStore._('failed-to-save-historical-data'), MESSAGE_TYPE.ERROR);
                },
            );
            FJProjectStore.setClipPublishedList(FJPublishManager.subId, newPublishedList);
            dispatch(updateDetailsItem({ ..._oldRow, publishedList: newPublishedList }));
            const nextProjects = FJProjectStore.mergeRecent(getState().startLanding.projects.items);
            if (nextProjects) {
                dispatch(setProjects(nextProjects));
            }
        }
        if (publishFailedList.length !== 0) {
            FJRequirementsUtil.eaLog('publish_window_publish_fail', {
                project_id: FJPublishManager.eventId,
                short_id: FJPublishManager.subId,
                fail_platform: publishFailedList,
                fail_reason: 'upload file fail',
            });
        }
    },
};

export default FJPublishAction;
